import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardText, MagnifyingGlass, Warning, PencilSimple, Trash } from "@phosphor-icons/react";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("-") : "—");
const fmtTs = (d) => {
    if (!d) return "—";
    const s = String(d);
    const day = s.slice(0, 10).split("-").reverse().join("-");
    const time = s.slice(11, 16);
    return time ? `${day} ${time}` : day;
};

export const AuditTrailDialog = ({ partyId, partyName }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState([]);
    const [asOf, setAsOf] = useState("");
    const [snap, setSnap] = useState(null);
    const [snapLoading, setSnapLoading] = useState(false);

    const loadTrail = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/ledger/${partyId}/audit-trail`);
            setEntries(data.entries || []);
        } catch (e) {
            toast.error("Failed to load audit trail");
        } finally {
            setLoading(false);
        }
    };

    const onOpenChange = (o) => {
        setOpen(o);
        if (o) { setSnap(null); setAsOf(""); loadTrail(); }
    };

    const runSnapshot = async () => {
        if (!asOf) { toast.error("Pick a date first"); return; }
        setSnapLoading(true);
        try {
            const { data } = await api.get(`/ledger/${partyId}/snapshot`, { params: { as_of: asOf } });
            setSnap(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to run snapshot");
        } finally {
            setSnapLoading(false);
        }
    };

    const rowCls = (e) => {
        if (e.deleted) return "bg-slate-100 text-slate-400";
        if (e.backdated) return "bg-rose-50";
        if (e.modified) return "bg-amber-50";
        return "";
    };

    const changeBadge = (ct) => {
        const map = {
            added: { cls: "bg-rose-100 text-rose-700", icon: <Warning size={11} weight="fill" />, label: "Added after" },
            modified: { cls: "bg-amber-100 text-amber-700", icon: <PencilSimple size={11} weight="fill" />, label: "Modified after" },
            deleted: { cls: "bg-slate-200 text-slate-600", icon: <Trash size={11} weight="fill" />, label: "Deleted after" },
        };
        const m = map[ct] || map.modified;
        return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.cls}`}>{m.icon} {m.label}</span>;
    };

    return (
        <>
            <Button
                type="button"
                variant="outline"
                data-testid="ledger-audit-trail-btn"
                onClick={() => onOpenChange(true)}
                className="h-9 border-slate-500 bg-transparent px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-200 hover:bg-slate-800"
                title="View the full audit trail for this party"
            >
                <ClipboardText size={15} className="mr-1.5" /> Audit Trail
            </Button>

            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="audit-trail-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ClipboardText size={18} className="text-indigo-600" /> Audit Trail — {partyName || "Party"}
                        </DialogTitle>
                    </DialogHeader>

                    {/* Legend */}
                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-rose-200" /> Backdated (created after entry date)</span>
                        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-amber-200" /> Modified after creation</span>
                        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-slate-200" /> Deleted</span>
                    </div>

                    {/* Compare Snapshot */}
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
                        <div className="text-xs font-semibold uppercase tracking-wider text-indigo-800">Compare Snapshot</div>
                        <p className="mt-0.5 text-[11px] text-slate-600">See the closing balance as it stood on a past date and what changed after it.</p>
                        <div className="mt-2 flex flex-wrap items-end gap-2">
                            <div>
                                <Label className="text-[10px] uppercase tracking-wider text-slate-500">As of date</Label>
                                <Input type="date" data-testid="audit-snapshot-date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="mt-1 h-9 w-44" />
                            </div>
                            <Button data-testid="audit-snapshot-run" onClick={runSnapshot} disabled={snapLoading} className="h-9 bg-indigo-700 text-white hover:bg-indigo-800">
                                <MagnifyingGlass size={14} className="mr-1.5" /> {snapLoading ? "Running…" : "Compare"}
                            </Button>
                        </div>

                        {snap && (
                            <div className="mt-3 space-y-3" data-testid="audit-snapshot-result">
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div className="rounded-md border border-slate-200 bg-white p-3">
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Closing balance as of {fmtDate(snap.as_of)} (as shown today)</div>
                                        <div data-testid="audit-snap-closing-today" className="mt-1 text-lg font-bold tabular-nums text-slate-900">{inr(snap.closing_balance_today)}</div>
                                    </div>
                                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">Reconstructed balance on that date (before later add/delete)</div>
                                        <div data-testid="audit-snap-closing-recon" className="mt-1 text-lg font-bold tabular-nums text-emerald-900">{inr(snap.reconstructed_closing_balance)}</div>
                                    </div>
                                </div>
                                <div className="text-xs text-slate-600">
                                    <span className="font-semibold">{snap.change_count}</span> entr{snap.change_count === 1 ? "y" : "ies"} dated on/before {fmtDate(snap.as_of)} changed afterwards
                                    {" · "}<span className="text-rose-700">{snap.added_count} added</span>
                                    {" · "}<span className="text-amber-700">{snap.modified_count} modified</span>
                                    {" · "}<span className="text-slate-600">{snap.deleted_count} deleted</span>
                                </div>
                                <div className="overflow-x-auto rounded-md border border-slate-200">
                                    <table className="min-w-full text-xs">
                                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                            <tr>
                                                <th className="px-2 py-1.5 text-left">Date</th>
                                                <th className="px-2 py-1.5 text-left">Description</th>
                                                <th className="px-2 py-1.5 text-right">Amount</th>
                                                <th className="px-2 py-1.5 text-left">Type</th>
                                                <th className="px-2 py-1.5 text-left">Change</th>
                                                <th className="px-2 py-1.5 text-left">When / By</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {snap.changes.length === 0 && (
                                                <tr><td colSpan={6} className="px-2 py-4 text-center text-emerald-600">No changes after this date — ledger is intact ✓</td></tr>
                                            )}
                                            {snap.changes.map((c, i) => (
                                                <tr key={`${c.id}-${i}`} data-testid={`audit-snap-change-${i}`}>
                                                    <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(c.date)}</td>
                                                    <td className="px-2 py-1.5 max-w-[200px] truncate" title={c.description}>{c.description}</td>
                                                    <td className="px-2 py-1.5 text-right tabular-nums">{inr(c.amount)}</td>
                                                    <td className="px-2 py-1.5">{c.type}</td>
                                                    <td className="px-2 py-1.5">{changeBadge(c.change_type)}</td>
                                                    <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">
                                                        {c.change_type === "deleted" ? `${fmtTs(c.deleted_at)} · ${c.deleted_by || "—"}` :
                                                            c.change_type === "added" ? `${fmtTs(c.created_at)} · ${c.created_by || "—"}` :
                                                                `${fmtTs(c.updated_at)} · ${c.updated_by || "—"}`}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Full trail */}
                    <div>
                        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">All entries ({entries.length})</div>
                        <div className="overflow-x-auto rounded-lg border border-slate-200">
                            <table className="min-w-full text-xs">
                                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="px-2 py-2 text-left">Date</th>
                                        <th className="px-2 py-2 text-left">Description</th>
                                        <th className="px-2 py-2 text-right">Amount</th>
                                        <th className="px-2 py-2 text-left">Type</th>
                                        <th className="px-2 py-2 text-left">Created On</th>
                                        <th className="px-2 py-2 text-left">Last Modified On</th>
                                        <th className="px-2 py-2 text-left">Modified By</th>
                                        <th className="px-2 py-2 text-left">Flags</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {loading && (<tr><td colSpan={8} className="px-2 py-6 text-center text-slate-400">Loading…</td></tr>)}
                                    {!loading && entries.length === 0 && (<tr><td colSpan={8} className="px-2 py-6 text-center text-slate-400">No entries.</td></tr>)}
                                    {!loading && entries.map((e, i) => (
                                        <tr key={`${e.id}-${i}`} data-testid={`audit-entry-${e.id}`} className={rowCls(e)}>
                                            <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(e.date)}</td>
                                            <td className="px-2 py-1.5 max-w-[220px] truncate" title={e.description}>
                                                {e.description}
                                                {e.deleted && <span data-testid={`audit-deleted-label-${e.id}`} className="ml-1.5 rounded bg-slate-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-700">Deleted</span>}
                                            </td>
                                            <td className="px-2 py-1.5 text-right tabular-nums">{inr(e.amount)}</td>
                                            <td className="px-2 py-1.5">{e.type}</td>
                                            <td className="px-2 py-1.5 whitespace-nowrap">{fmtTs(e.created_at)}</td>
                                            <td className="px-2 py-1.5 whitespace-nowrap">{e.updated_at ? fmtTs(e.updated_at) : "—"}</td>
                                            <td className="px-2 py-1.5 whitespace-nowrap">{e.updated_by || e.created_by || "—"}</td>
                                            <td className="px-2 py-1.5">
                                                <div className="flex flex-wrap gap-1">
                                                    {e.backdated && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[9px] font-semibold text-rose-700">Backdated</span>}
                                                    {e.modified && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-semibold text-amber-700">Modified</span>}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AuditTrailDialog;
